import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Topbar from '../components/Topbar.jsx';
import Toolbar from '../components/Toolbar.jsx';
import ExtraToolbar from '../components/ExtraToolbar.jsx';
import Statusbar from '../components/Statusbar.jsx';
import DicomCanvas from '../components/DicomCanvas.jsx';
import ThumbnailStrip from '../components/ThumbnailStrip.jsx';
import RightPanel from '../components/RightPanel.jsx';
import CineControls from '../components/CineControls.jsx';
import MeasurementLayer from '../components/MeasurementLayer.jsx';
import AnnotationLayer from '../components/AnnotationLayer.jsx';
import AIRegionsOverlay from '../components/AIRegionsOverlay.jsx';
import CalibrationDialog from '../components/CalibrationDialog.jsx';
import KeyboardCheatsheet from '../components/KeyboardCheatsheet.jsx';
import ComparisonView from '../components/ComparisonView.jsx';
import TemplatePicker from '../components/TemplatePicker.jsx';

import {
  getStudy, generateAiRead, signReport, downloadDicom,
  listMeasurements, saveMeasurement, deleteMeasurement,
  listAnnotations, saveAnnotation, deleteAnnotation,
  listKeyImages, toggleKeyImage,
  downloadStudyZip,
} from '../api/client.js';
import { parseDICOM } from '../lib/dicom-parser.js';
import { pxDist } from '../lib/measurements.js';
import * as keyboard from '../lib/keyboard.js';
import { downloadReportPdf } from '../lib/pdf-export.js';

import styles from './Viewer.module.css';

const MEASURE_TOOLS = new Set(['distance', 'angle', 'cobb', 'ellipse', 'calibration']);
const ANNOT_TOOLS   = new Set(['arrow', 'text', 'rect', 'pin']);
const VIEW_TOOLS    = new Set(['wl', 'pan', 'zoom']);

export default function Viewer() {
  const { uid } = useParams();

  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [study, setStudy]         = useState(null);
  const [images, setImages]       = useState([]);
  const [aiRead, setAiRead]       = useState(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [parsed, setParsed]       = useState(null);

  const [tool, setTool]           = useState('wl');
  const [wc, setWc]               = useState(400);
  const [ww, setWw]               = useState(1800);
  const [invert, setInvert]       = useState(false);
  const [rotation, setRotation]   = useState(0);
  const [panX, setPanX]           = useState(0);
  const [panY, setPanY]           = useState(0);
  const [zoom, setZoom]           = useState(1);

  const [aiState, setAiState]         = useState('none');
  const [measurements, setMeasurements] = useState([]);
  const [annotations, setAnnotations]   = useState([]);
  const [keyImages, setKeyImages]       = useState([]);
  const [templates, setTemplates]       = useState([]);     // in-memory only (client-side planning)
  const [mmPerPx, setMmPerPx]           = useState(null);

  const [calibrationPending, setCalibrationPending] = useState(null);
  const [cheatOpen, setCheatOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  const canvasSlotRef = useRef(null);
  const [canvasSize, setCanvasSize] = useState({ w: 0, h: 0 });

  // --- Load study ----------------------------------------------------------
  useEffect(() => {
    let aborted = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await getStudy(uid);
        if (aborted) return;
        setStudy(r.study); setImages(r.images || []); setAiRead(r.aiRead || null);
        if (r.aiRead?.finalized) setAiState('signed');
        else if (r.aiRead)       setAiState('generated');
        else                     setAiState('none');

        const [meas, annots, keys] = await Promise.all([
          listMeasurements(uid).catch(() => []),
          listAnnotations(uid).catch(() => []),
          listKeyImages(uid).catch(() => []),
        ]);
        if (aborted) return;
        setMeasurements(meas);
        setAnnotations(annots);
        setKeyImages(keys);
        // Look for a calibration measurement.
        const cal = meas.find(m => m.kind === 'calibration' && m.value_mm && Array.isArray(m.geometry?.points) && m.geometry.points.length >= 2);
        if (cal) {
          const px = pxDist(cal.geometry.points[0], cal.geometry.points[1]);
          if (px > 0) setMmPerPx(cal.value_mm / px);
        }
      } catch (err) {
        if (!aborted) setError(err.message || 'Failed to load study');
      } finally {
        if (!aborted) setLoading(false);
      }
    })();
    return () => { aborted = true; };
  }, [uid]);

  // --- Load active image ---------------------------------------------------
  useEffect(() => {
    let aborted = false;
    const img = images[activeIdx];
    if (!img?.sasUrl) return;
    (async () => {
      try {
        const buf = await downloadDicom(img.sasUrl);
        if (aborted) return;
        const p = parseDICOM(buf);
        if (aborted) return;
        setParsed(p);
        if (Number.isFinite(p.windowCenter) && Number.isFinite(p.windowWidth) && p.windowWidth > 0) {
          setWc(p.windowCenter); setWw(p.windowWidth);
        }
      } catch (err) { if (!aborted) setError(err.message || 'DICOM render failed'); }
    })();
    return () => { aborted = true; };
  }, [images, activeIdx]);

  // --- Canvas size observer ------------------------------------------------
  useEffect(() => {
    const el = canvasSlotRef.current;
    if (!el) return;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      setCanvasSize({ w: rect.width, h: rect.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  // --- Keyboard shortcuts --------------------------------------------------
  useEffect(() => {
    keyboard.init();
    const dispose = keyboard.register({
      'w':   () => setTool('wl'),
      'p':   () => setTool('pan'),
      'z':   () => setTool('zoom'),
      'm':   () => setTool('distance'),
      'a':   () => setTool('angle'),
      'shift+c': () => setTool('cobb'),
      'n':   () => setTool('arrow'),
      't':   () => setTool('text'),
      'r':   () => setRotation(r => (r + 90) % 360),
      'i':   () => setInvert(v => !v),
      'escape': () => { setTool('wl'); setPanX(0); setPanY(0); setZoom(1); setRotation(0); setInvert(false); },
      'arrowleft':  () => setActiveIdx(i => Math.max(0, i - 1)),
      'arrowright': () => setActiveIdx(i => Math.min((images.length || 1) - 1, i + 1)),
      'k':   () => toggleKey(),
      'shift+s': () => handleSign({}),
      '?':   () => setCheatOpen(true),
      'shift+/': () => setCheatOpen(true),
      '1': () => { setWc(400);  setWw(1800); },
      '2': () => { setWc(-600); setWw(1600); },
      '3': () => { setWc(40);   setWw(80);   },
      '4': () => { setWc(60);   setWw(400);  },
      '5': () => { setWc(400);  setWw(1500); },
      '6': () => { setWc(50);   setWw(350);  },
    });
    return dispose;
  }, [images.length, activeIdx, aiRead]);

  // --- Actions -------------------------------------------------------------
  async function handleGenerate() {
    setAiState('generating');
    try {
      const r = await generateAiRead(uid);
      setAiRead(r);
      setAiState('generated');
    } catch (err) { setAiState('none'); setError(err.message || 'AI read failed'); }
  }

  async function handleSign(edits) {
    if (!aiRead?.id) return;
    try {
      await signReport(uid, aiRead.id, edits);
      setAiState('signed');
      setAiRead(r => ({ ...r, finalized: true, signed_at: new Date().toISOString() }));
    } catch (err) { setError(err.message || 'Sign failed'); }
  }

  function handleToolAction(action) {
    if (action === 'rotate') setRotation(r => (r + 90) % 360);
    else if (action === 'invert') setInvert(v => !v);
    else if (action === 'reset')  { setRotation(0); setInvert(false); setPanX(0); setPanY(0); setZoom(1); }
    else if (action === 'compare')   setCompareOpen(true);
    else if (action === 'pdf')       doPdfExport();
    else if (action === 'zip')       downloadStudyZip(uid).catch(e => setError(e.message));
    else if (action === 'shortcuts') setCheatOpen(true);
  }

  async function onCreateMeasurement(m) {
    try {
      const resp = await saveMeasurement(uid, { ...m, image_index: activeIdx, geometry: { points: m.points, closed: false } });
      setMeasurements(prev => [...prev, { id: resp.id, kind: m.kind, image_index: activeIdx, geometry: { points: m.points }, value_mm: m.value_mm, value_deg: m.value_deg, label: m.label, created_by: 'me', created_at: new Date().toISOString() }]);
    } catch (err) { setError(err.message); }
  }

  async function onCalibrationConfirm({ points, mm, pxDist, mmPerPx: perPx }) {
    setMmPerPx(perPx);
    try {
      const resp = await saveMeasurement(uid, {
        image_index: activeIdx,
        kind: 'calibration',
        geometry: { points },
        value_mm: mm,
        label: `${mm} mm calibration`,
      });
      setMeasurements(prev => [...prev, { id: resp.id, kind: 'calibration', image_index: activeIdx, geometry: { points }, value_mm: mm, label: `${mm} mm calibration`, created_at: new Date().toISOString(), created_by: 'me' }]);
    } catch (err) { setError(err.message); }
    setCalibrationPending(null);
    setTool('wl');
  }

  async function onCreateAnnotation(payload) {
    try {
      const resp = await saveAnnotation(uid, { ...payload, image_index: activeIdx });
      setAnnotations(prev => [...prev, { id: resp.id, image_index: activeIdx, ...payload, created_at: new Date().toISOString(), created_by: 'me' }]);
    } catch (err) { setError(err.message); }
  }

  async function toggleKey() {
    const already = keyImages.find(ki => ki.image_index === activeIdx);
    try {
      if (already) {
        await fetch(`/api/study/${encodeURIComponent(uid)}/key-images?image_index=${activeIdx}`, { method: 'DELETE' });
        setKeyImages(prev => prev.filter(ki => ki.id !== already.id));
      } else {
        const resp = await toggleKeyImage(uid, activeIdx);
        setKeyImages(prev => [...prev, { id: resp.id, image_index: activeIdx, marked_at: resp.marked_at, marked_by: 'me' }]);
      }
    } catch (err) { setError(err.message); }
  }

  async function doPdfExport() {
    const canvas = document.querySelector('canvas');
    const dataUrl = canvas ? canvas.toDataURL('image/jpeg', 0.85) : null;
    downloadReportPdf({
      study, aiRead,
      measurements: measurements.filter(m => m.kind !== 'calibration'),
      canvasDataUrl: dataUrl,
    });
  }

  function handleDictate(text) {
    if (!text) return;
    if (!aiRead) {
      // stash a free-form finding
      setAiRead({ findings: [text], impression: '', recommendations: [], finalized: false });
      setAiState('generated');
      return;
    }
    setAiRead(r => ({ ...r, impression: ((r.impression || '') + ' ' + text).trim() }));
  }

  const visibleMeasurements = measurements.filter(m => m.image_index === activeIdx);
  const visibleAnnots = annotations.filter(a => a.image_index === activeIdx);
  const aiRegions = aiRead?.regions || [];
  const isKey = keyImages.some(ki => ki.image_index === activeIdx);

  const overlays = useMemo(() => {
    if (!study || !parsed) return {};
    const tl = [
      study.patient_name || '',
      [study.modality, study.study_date, study.ordering_provider].filter(Boolean).join(' · '),
    ].filter(Boolean).join('\n');
    const tr = [
      Number.isFinite(wc) ? `WC ${Math.round(wc)}` : '',
      Number.isFinite(ww) ? `WW ${Math.round(ww)}` : '',
      mmPerPx ? `${(mmPerPx * 1000).toFixed(2)} µm/px` : 'uncalibrated',
    ].filter(Boolean).join('\n');
    const bl = `${parsed.cols} × ${parsed.rows}`;
    const br = `${parsed.bitsAllocated || '?'} bit\n${activeIdx + 1} / ${images.length || 1}${isKey ? '  ★' : ''}`;
    return { topLeft: tl, topRight: tr, bottomLeft: bl, bottomRight: br };
  }, [study, parsed, wc, ww, mmPerPx, activeIdx, images.length, isKey]);

  const breadcrumb = study ? `${study.accession_number || study.study_instance_uid} · ${study.study_description || ''}` : 'Loading…';
  const canvasTool = VIEW_TOOLS.has(tool) ? tool : null; // DicomCanvas pointer behavior

  return (
    <div className={styles.shell}>
      <Topbar breadcrumb={breadcrumb} locationLabel={study ? `${study.modality || ''} · ${study.location_ae || ''}` : ''} />
      <ExtraToolbar activeTool={tool} onSelect={setTool} onAction={handleToolAction} />
      <div className={styles.main}>
        <Toolbar active={VIEW_TOOLS.has(tool) ? tool : null} onSelect={setTool} onAction={handleToolAction} />
        <div className={styles.canvasCol} ref={canvasSlotRef}>
          {loading ? (
            <div className={styles.placeholder}>Loading…</div>
          ) : error ? (
            <div className={styles.placeholder}>{error}</div>
          ) : (
            <>
              <DicomCanvas
                parsed={parsed}
                tool={canvasTool}
                wc={wc} ww={ww}
                onWindowChange={(nWc, nWw) => { setWc(nWc); setWw(nWw); }}
                invert={invert}
                rotation={rotation}
                overlays={overlays}
              />
              {parsed && (
                <>
                  <AIRegionsOverlay
                    regions={aiRegions}
                    imageWidth={parsed.cols} imageHeight={parsed.rows}
                    canvasWidth={canvasSize.w} canvasHeight={canvasSize.h}
                    panX={panX} panY={panY} zoom={zoom} rotation={rotation}
                  />
                  <MeasurementLayer
                    measurements={visibleMeasurements}
                    activeKind={MEASURE_TOOLS.has(tool) ? tool : null}
                    mmPerPx={mmPerPx}
                    imageWidth={parsed.cols} imageHeight={parsed.rows}
                    canvasWidth={canvasSize.w} canvasHeight={canvasSize.h}
                    panX={panX} panY={panY} zoom={zoom} rotation={rotation}
                    onCreate={onCreateMeasurement}
                    onCalibrationNeedValue={(points) => setCalibrationPending({ points, pxDist: pxDist(points[0], points[1]) })}
                  />
                  <AnnotationLayer
                    annotations={visibleAnnots}
                    activeKind={ANNOT_TOOLS.has(tool) ? tool : null}
                    imageWidth={parsed.cols} imageHeight={parsed.rows}
                    canvasWidth={canvasSize.w} canvasHeight={canvasSize.h}
                    panX={panX} panY={panY} zoom={zoom} rotation={rotation}
                    onCreate={onCreateAnnotation}
                    onTextInput={(payload) => {
                      const text = window.prompt('Annotation text:');
                      if (text) onCreateAnnotation({ ...payload, text });
                    }}
                  />
                  <TemplatePicker
                    mmPerPx={mmPerPx}
                    imageWidth={parsed.cols} imageHeight={parsed.rows}
                    canvasWidth={canvasSize.w} canvasHeight={canvasSize.h}
                    panX={panX} panY={panY} zoom={zoom} rotation={rotation}
                    templates={templates}
                    onPlace={(tpl) => setTemplates(prev => {
                      const idx = prev.findIndex(p => p.id === tpl.id);
                      if (idx >= 0) { const next = [...prev]; next[idx] = tpl; return next; }
                      return [...prev, tpl];
                    })}
                    onRemove={(id) => setTemplates(prev => prev.filter(p => p.id !== id))}
                  />
                </>
              )}
              <CineControls
                total={images.length}
                activeIndex={activeIdx}
                onSelect={setActiveIdx}
                onToggleKey={toggleKey}
                isKey={isKey}
              />
              <ThumbnailStrip images={images} activeIndex={activeIdx} onSelect={setActiveIdx} />
            </>
          )}
        </div>
        <RightPanel
          study={study} parsed={parsed} aiRead={aiRead} aiState={aiState}
          onGenerate={handleGenerate} onSign={handleSign} onDictate={handleDictate}
          wc={wc} ww={ww} onWindowChange={(n, d) => { setWc(n); setWw(d); }}
          keyImages={keyImages} onJumpToKeyImage={setActiveIdx}
        />
      </div>
      <Statusbar
        left={study?.patient_name ? `${study.patient_name} · MRN ${study.patient_id || ''}` : ''}
        right={study?.study_instance_uid || ''}
      />

      {calibrationPending && (
        <CalibrationDialog
          points={calibrationPending.points}
          pxDist={calibrationPending.pxDist}
          onCancel={() => { setCalibrationPending(null); setTool('wl'); }}
          onConfirm={onCalibrationConfirm}
        />
      )}
      {cheatOpen && <KeyboardCheatsheet onClose={() => setCheatOpen(false)} />}
      {compareOpen && <ComparisonView primaryUid={uid} onClose={() => setCompareOpen(false)} />}
    </div>
  );
}
